import { MouseEvent } from 'react';
import useDataQuery from '../../../../../hooks/useDataQuery/useDataQuery';
import { useUserContextPreferences } from '../../../../../hooks/useUserContextPreferences';
import { FilterPreferenceRecognizedLists } from '../../../../../services/modules/userContextPreferences/userContextPreferences.types';
import { ExtractPaginationType } from '../../../../../services/types';
import { Pagination } from '../../../../../types/pagination.type';
import { ArrayUtil } from '../../../../../utils/array.utils';
import { GenericQueryOutput } from '../../../../Crud/filters/hooks/multiOptionsSelectorFilter/useMultiDynamicOptionsSelector/useMultiDynamicOptionsSelector.types';
import { MultiDynamicOptionsCreatorFilter } from '../../../../Crud/filters/sharedBetweenFilters/types';
import { useFilterV3Context } from '../../../contexts/FilterV3.context';
import SelectedChipCreator from './SelectedChipCreator';

export interface SelectedCreatorListProps {
  filter: MultiDynamicOptionsCreatorFilter;
  list: FilterPreferenceRecognizedLists;
}

const getFilterKey = (filter: MultiDynamicOptionsCreatorFilter, data: Record<string, object>) => {
  return `${filter.multiDynamicOptionsCreatorArgs.keys.idsPrefix}${
    data[filter.multiDynamicOptionsCreatorArgs.keys.filterId]
  }`;
};

const SelectedCreatorList = ({ filter, list }: SelectedCreatorListProps) => {
  const { handleClickSelectedFilterByIndex, options } = useFilterV3Context();

  const { getDefaultFiltersToList } = useUserContextPreferences();
  const defaultFiltersToList = getDefaultFiltersToList(list);

  const { data: rawDynamicData } = useDataQuery<Pagination<
    ExtractPaginationType<GenericQueryOutput>
  > | null>({
    queryKey: filter.multiDynamicOptionsCreatorArgs.filtersQueryKey(
      filter.multiDynamicOptionsCreatorArgs.filtersQueryParams,
    ),
    queryFn: async () => {
      const data = await filter.multiDynamicOptionsCreatorArgs.filtersQueryFn(
        filter.multiDynamicOptionsCreatorArgs.filtersQueryParams,
      );
      if (!data.ok || !data.data) {
        throw new Error('Network response was not ok');
      }
      return data?.data;
    },
  });

  const selectedFilters = !!defaultFiltersToList
    ? rawDynamicData?.data.filter((row) => {
        const key = getFilterKey(filter, row);
        return Object.hasOwn(defaultFiltersToList, key);
      })
    : [];

  const handleClick = (event: MouseEvent<HTMLElement>, data: Record<string, unknown>) => {
    const filterIndex = options.findIndex((option) => option.title === filter.title);

    handleClickSelectedFilterByIndex({
      event,
      filterIndex,
      data,
      enableReturnToList: false,
    });
  };

  return (
    <>
      {selectedFilters?.map((data) => {
        const filterKey = getFilterKey(filter, data);
        const selectedIds = ArrayUtil.getAsArray(defaultFiltersToList?.[filterKey]);
        const key = `${filterKey}:${selectedIds.join('|')}`;

        return (
          <SelectedChipCreator
            key={key}
            onClick={(event) => handleClick(event, data)}
            list={list}
            data={data}
            filter={filter}
            filterKey={filterKey}
          />
        );
      })}
    </>
  );
};

export default SelectedCreatorList;
